import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'
import type { UploadBridge } from '@/features/editor/lib/uploadBridge'
import { CanvasDropzone } from '@/features/editor/components/shared/CanvasDropzone'
import { UploadProgressOverlay } from '@/features/editor/components/shared/UploadProgressOverlay'
import { useSmoothProgress } from '@/features/editor/components/shared/useSmoothProgress'

/** Холст редактора, где автор может править открытый экран (не миниатюра, не прохождение). */
const CanvasVideoDropContext = createContext(false)

export const CanvasVideoDrop = ({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) => <CanvasVideoDropContext.Provider value={enabled}>{children}</CanvasVideoDropContext.Provider>

/** Можно ли загружать файл прямо на холсте (видео и картинка блока). */
export const useCanvasVideoDrop = (): boolean => useContext(CanvasVideoDropContext)

interface Props {
  bridge: UploadBridge
  accept: Record<string, string[]>
  label: string
  subtitle: string
}

/**
 * Пустой экран на холсте: та же фиолетовая зона, что у картинки составного блока.
 * Клик открывает выбор файла, перетаскивание принимает его — грузит зона в панели
 * блока (`bridge`), а сюда возвращается прогресс.
 */
export const CanvasBridgeDrop = ({ bridge, accept, label, subtitle }: Props) => {
  const raw = useSyncExternalStore(bridge.subscribeProgress, bridge.getProgress)
  const progress = useSmoothProgress(raw)
  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple: false,
    noClick: true,
    onDrop: (files) => {
      const file = files[0]
      if (file) bridge.request(file)
    },
  })
  return (
    <CanvasDropzone
      icon={Upload}
      label={label}
      subtitle={subtitle}
      rootProps={getRootProps({ onClick: () => bridge.request(null) })}
      inputProps={getInputProps()}
      disabled={false}
      uploading={raw !== null}
      progress={progress}
    />
  )
}

/** Замена ролика: на холсте играет плеер, зоны нет — прогресс идёт поверх него. */
export const CanvasBridgeProgress = ({ bridge }: { bridge: UploadBridge }) => {
  const raw = useSyncExternalStore(bridge.subscribeProgress, bridge.getProgress)
  const progress = useSmoothProgress(raw)
  return <UploadProgressOverlay progress={raw === null ? null : progress} />
}
